<template>
  <section class="partners-slider" data-aos="fade-up" data-aos-delay="100">
    <div class="slider-container" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
      <!-- Slides wrapper -->
      <div
        class="slides-wrapper"
        :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
        dir="ltr"
      >
        <div
          class="slide"
          v-for="(item, index) in partners_stratigic"
          :key="index"
          :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'"
        >
          <!-- 🔹 Entire slide is clickable -->
          <a class="partner-item" :href="item?.link" target="_blank">
            <div class="logo">
              <img
                :src="$store.state.server + item.img"
                :alt="item.name_en"
                class="img-fluid"
              />
            </div>
            <div class="content">
              <h3>
                {{ $i18n.locale === "ar" ? item.name_ar : item.name_en }}
              </h3>
              <p>
                {{
                  $i18n.locale === "ar"
                    ? item.description_ar
                    : item.description_en
                }}
              </p>
            </div>
          </a>
        </div>
      </div>

      <!-- Controls -->
      <button class="nav-btn prev" @click="prevSlide">‹</button>
      <button class="nav-btn next" @click="nextSlide">›</button>

      <!-- Pagination dots -->
      <div class="dots">
        <span
          v-for="(dot, i) in partners_stratigic.length"
          :key="i"
          :class="{ active: i === currentIndex }"
          @click="goToSlide(i)"
        ></span>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from "vue";

export default {
  name: "PartnersSlider",
  props: {
    partners_stratigic: {
      type: Array,
      required: true,
    },
  },
  setup(props) {
    const currentIndex = ref(0);
    let intervalId = null;

    const nextSlide = () => {
      currentIndex.value =
        (currentIndex.value + 1) % props.partners_stratigic.length;
    };

    const prevSlide = () => {
      currentIndex.value =
        (currentIndex.value - 1 + props.partners_stratigic.length) %
        props.partners_stratigic.length;
    };

    const goToSlide = (index) => {
      currentIndex.value = index;
    };

    onMounted(() => {
      intervalId = setInterval(nextSlide, 4000);
    });

    onBeforeUnmount(() => {
      clearInterval(intervalId);
    });

    return {
      currentIndex,
      nextSlide,
      prevSlide,
      goToSlide,
    };
  },
};
</script>

<style scoped>
/* Container */
.slider-container {
  position: relative;
  overflow: hidden;
  width: 100%;
  background: var(--color-background);
}

/* Slides */
.slides-wrapper {
  display: flex;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
}

.slide {
  min-width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
}

/* Partner item */
.partner-item {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  max-width: 900px;
  width: 100%;
  transition: transform 0.3s ease;
  text-decoration: none; /* remove underline */
  color: inherit; /* keep text color */
  cursor: pointer; /* show clickable cursor */
}

.partner-item[dir="rtl"] {
  flex-direction: row-reverse;
  text-align: right;
}

.partner-item:hover {
  transform: translateY(-4px);
}

/* Logo */
.logo img {
  max-width: 260px;
  max-height: 150px;
  object-fit: contain;
  transition: transform 0.3s ease;
}
.partner-item:hover .logo img {
  transform: scale(1.05);
}

/* Content */
.content {
  flex: 1;
}

.content h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-secondary);
  margin-bottom: 0.6rem;
  transition: color 0.3s ease;
}

.partner-item:hover .content h3 {
  color: var(--color-primary);
}

.content p {
  font-size: 1rem;
  color: var(--color-default);
  opacity: 0.9;
  line-height: 1.6;
  text-align: justify;
}

/* Navigation buttons */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: var(--color-primary);
  color: white;
  border: none;
  width: 42px;
  height: 42px;
  cursor: pointer;
  border-radius: 50%;
  font-size: 1.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
}
.nav-btn:hover {
  background: var(--color-secondary);
  transform: translateY(-50%) scale(1.1);
}

/* Logical positioning for RTL/LTR */
.prev {
  inset-inline-start: 1rem;
}
.next {
  inset-inline-end: 1rem;
}

/* Dots */
.dots {
  text-align: center;
  margin: 1.5rem 0;
}
.dots span {
  display: inline-block;
  width: 12px;
  height: 12px;
  background: var(--color-default);
  border-radius: 50%;
  margin: 0 5px;
  cursor: pointer;
  opacity: 0.4;
  transition: opacity 0.3s, transform 0.3s, background 0.3s;
}
.dots span.active {
  opacity: 1;
  transform: scale(1.3);
  background: var(--color-secondary);
}

/* Responsive */
@media (max-width: 768px) {
  .partner-item {
    flex-direction: column;
    text-align: center;
  }
  .partner-item[dir="rtl"] {
    flex-direction: column;
    text-align: center;
  }
  .content h3 {
    font-size: 1.1rem;
  }
  .content p {
    font-size: 0.9rem;
  }
}
</style>
